/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/library-risk.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import AuditEnumValues from '../definitions/enums';
import { NamedItemType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import { getFieldChangeItems, getProcessedFieldChangeItems } from 'views/pages/admin/audit-log/service';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { libraryRiskHistory_OpraQueries_libraryRiskHistory_LibraryRisk } from '@queries/generated/audit.generated';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { riskObservationsRisks_OpraQueries_risks_Risk } from '@queries/generated/legacy-risk-assessment.generated';

const LibraryRiskRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem, record }: any) => {
    const associations: any = {
        LibraryControlIds: {
            itemField: 'libraryControls',
            name: 'Library Control',
            nameResolver: (control: any) => control.description,
        },
        RiskLibraryCategoryIds: {
            itemField: 'riskLibraryCategory',
            name: 'Categories',
            customMessage: 'Library Category(s)',
            nameResolver: (category: any) => category.name,
        },
    };

    // ReviewFrequencyPeriod & ReviewFrequencyUnits are reversed here due to being incorrect in the API
    const refactoredOldRecord = {
        ...oldRecord,
        ReviewFrequencyPeriod: oldRecord.ReviewFrequencyUnits,
        ReviewFrequencyUnits: oldRecord.ReviewFrequencyPeriod,
        ReviewRequired: record.system ? undefined : oldRecord.ReviewRequired,
    };
    const refactoredNewRecord = {
        ...newRecord,
        ReviewFrequencyPeriod: newRecord.ReviewFrequencyUnits === 0 ? undefined : newRecord.ReviewFrequencyUnits,
        ReviewFrequencyUnits: newRecord.ReviewFrequencyPeriod === 0 ? undefined : newRecord.ReviewFrequencyPeriod,
        ReviewRequired: record.system ? undefined : newRecord.ReviewRequired,
    };

    if (record?.system) {
        delete refactoredOldRecord.DisplayOrder;
        delete refactoredNewRecord.DisplayOrder;
    }

    const { data: vendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.VENDOR,
            timestamp: record.created,
        },
        fetchPolicy: 'no-cache',
    });

    const formatCountries = (recordData: { Code: string; Name: string }, index: number) => {
        if (!recordData) {
            return null;
        }

        return `Order: ${index + 1}\nName: ${recordData?.Name}`;
    };

    const formatVendorIds = (recordData: any, index: number) => {
        if (!recordData) {
            return null;
        }

        const data = recordData as { $oid: string };
        const vendor = vendorData?.namedItemsByType?.find((rv) => rv?.id === data.$oid);

        if (!vendor) {
            return null;
        }

        return `Order: ${index + 1}\n RecordId: ${vendor?.recordId}\nName: ${vendor?.name}`;
    };

    const enums: any = {
        ReviewFrequencyPeriod: AuditEnumValues.TimeFrequency,
    };

    const arrayOfObjectsV2: any = {
        Countries: {
            valueResolver: formatCountries,
            compareField: 'Code',
        },
        VendorIds: {
            valueResolver: formatVendorIds,
            compareField: '$oid',
        },
    };
    const lookups: any = {
        ResponsibleFunctionId: (libraryRisk: libraryRiskHistory_OpraQueries_libraryRiskHistory_LibraryRisk) =>
            libraryRisk?.responsibleFunction?.name,
        RiskCategoryId: (libraryRisk: libraryRiskHistory_OpraQueries_libraryRiskHistory_LibraryRisk) =>
            libraryRisk?.riskCategory?.name,
        ControlStrategyId: (risk: riskObservationsRisks_OpraQueries_risks_Risk) => risk?.controlStrategy?.name,
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            lookups,
            enums,
            associations,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { LibraryRiskRecordChangeGrid };
